import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

import { fetchDividendRuns } from "../services/dividendService";

const TARGET_OPTIONS = [
  { value: "ALL", label: "All Members" },
  { value: "ACTIVE", label: "Active Members" },
  { value: "BORROWERS", label: "Borrowers" },
];

const tzs = (value) => {
  const amount = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(amount)) return "TZS 0";
  return `TZS ${new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(amount)}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? dateStr : date.toLocaleDateString("en-GB");
};

const prettifyTarget = (target) => {
  if (!target) return "All Members";
  const normalized = String(target).toUpperCase();
  if (normalized === "ALL") return "All Members";
  if (normalized === "ACTIVE") return "Active Members";
  if (normalized === "BORROWERS") return "Borrowers";
  return target;
};

const prettifySource = (source) => {
  if (!source) return "Mixed";
  const normalized = String(source).toUpperCase();
  if (normalized === "INTEREST") return "Interest";
  if (normalized === "PENALTY") return "Penalty";
  if (normalized === "DEFAULT") return "Default";
  return source;
};

export default function DividendGovernancePanel({ settings, onChange }) {
  const { t } = useTranslation();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [recentRuns, setRecentRuns] = useState([]);

  useEffect(() => {
    let mounted = true;
    const loadRuns = async () => {
      setHistoryLoading(true);
      try {
        const runs = await fetchDividendRuns();
        if (mounted) {
          setRecentRuns(Array.isArray(runs) ? runs.slice(0, 6) : []);
          setHistoryError("");
        }
      } catch (error) {
        if (mounted) {
          setHistoryError(error.message || "Unable to load dividend history.");
        }
      } finally {
        if (mounted) setHistoryLoading(false);
      }
    };
    loadRuns();
    return () => {
      mounted = false;
    };
  }, []);

  const activeSources = useMemo(() => ([
    settings.autoDividendIncludeInterest && t("Interest"),
    settings.autoDividendIncludePenalties && t("Penalties"),
    settings.autoDividendIncludeDefaults && t("Defaults"),
  ].filter(Boolean)), [settings.autoDividendIncludeDefaults, settings.autoDividendIncludeInterest, settings.autoDividendIncludePenalties, t]);

  const patch = (values) => onChange(values);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t("Automatic Dividend Governance")}
          </Typography>
          <Tooltip title={t("Chairpersons configure who receives automatically distributed dividends and which income sources are eligible. The system triggers distribution instantly when enabled income is posted.")}>
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("No manual initiation is required. Once configured, the system distributes dividends automatically whenever enabled interest, penalty, or default income is posted for this group.")}
        </Typography>

        <Alert severity={activeSources.length ? "success" : "warning"} sx={{ borderRadius: 2, mb: 2 }}>
          {activeSources.length
            ? t("Automatic dividend distribution is active for: {{sources}}.", { sources: activeSources.join(", ") })
            : t("Automatic dividend distribution is currently disabled. Enable at least one income source below.")}
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("Distribution Target")}</InputLabel>
              <Select
                label={t("Distribution Target")}
                value={settings.autoDividendTarget}
                onChange={(e) => patch({ autoDividendTarget: e.target.value })}
              >
                {TARGET_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings.autoDividendIncludeInterest)}
                    onChange={(e) => patch({ autoDividendIncludeInterest: e.target.checked })}
                  />
                }
                label={t("Include interest")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings.autoDividendIncludePenalties)}
                    onChange={(e) => patch({ autoDividendIncludePenalties: e.target.checked })}
                  />
                }
                label={t("Include penalties")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(settings.autoDividendIncludeDefaults)}
                    onChange={(e) => patch({ autoDividendIncludeDefaults: e.target.checked })}
                  />
                }
                label={t("Include defaults")}
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {t("Recent Dividend Runs")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("These are the latest completed dividend distributions for this group, including automatically triggered runs.")}
        </Typography>

        {historyError && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            {historyError}
          </Alert>
        )}

        {historyLoading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Date")}</TableCell>
                  <TableCell>{t("Mode")}</TableCell>
                  <TableCell>{t("Source")}</TableCell>
                  <TableCell>{t("Target")}</TableCell>
                  <TableCell>{t("Recipients")}</TableCell>
                  <TableCell align="right">{t("Net Pool")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentRuns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      {t("No dividend runs recorded yet.")}
                    </TableCell>
                  </TableRow>
                ) : recentRuns.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>{formatDate(run.executed_at || run.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={run.is_automatic ? "success" : "default"}
                        label={run.is_automatic ? t("Automatic") : t("Manual")}
                      />
                    </TableCell>
                    <TableCell>{t(prettifySource(run.distribution_source))}</TableCell>
                    <TableCell>{t(prettifyTarget(run.target_type))}</TableCell>
                    <TableCell>{run.total_recipients ?? 0}</TableCell>
                    <TableCell align="right">{tzs(run.net_pool)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
